import { useAuth } from '@opendoor/auth-fe';
import { Box } from '@opendoor/bricks-next';
import { Entry } from 'contentful';
import { useRouter } from 'next/router';

import { Awaited, EntryComponent, LoaderFn } from '../../cms/entries/entries';
import {
  ILandingPageFields,
  ILpComponentHeroV2,
  ILpComponentHeroV2WithMarkers,
} from '../../declarations/contentful';
import { getMarketDisplayNameForHeroV2 } from '../../helpers/markets';
import { PictureSourceSpec } from '../shared/performance/pictures';
import { HeroImages } from './shared/hero/HeroImages';
import {
  getStickyCTAFromRoot,
  HeroProvider,
  preloadHeroImages,
  useStickyCTA,
} from './shared/hero/HeroShared';
import { HeroTitleAndAddress } from './shared/hero/HeroTitleAndAddress';
import { HeroV2CustomizationProps } from './shared/hero/heroTypes';
import { PressBox } from './shared/hero/PressBox';
import { SocialProofBox } from './SocialProofBox';
import { StickyCTAUtility } from './StickyCTAUtility/StickyCTAUtility';

// Re-export for backward compatibility
export type { HeroV2CustomizationProps } from './shared/hero/heroTypes';

const RenderHeroV2 = (
  entry: ILpComponentHeroV2 | ILpComponentHeroV2WithMarkers,
  resolvedData?: Awaited<ReturnType<typeof heroV2Loader>>,
  customization?: HeroV2CustomizationProps,
) => {
  const router = useRouter();
  const { authentication } = useAuth();

  const { fields } = entry;
  const isTreatment = fields.analyticsId === 'big-swing-hero-v2';
  const hideStickyCta =
    customization?.hideStickyCtaOverride !== undefined
      ? Boolean(customization.hideStickyCtaOverride)
      : resolvedData?.stickyCTA === 'None' || authentication.state === 'authenticated';
  const headlineMarketIdentifier = router.query.headline_market_name as string | undefined;
  const headlineMarketDisplayName = getMarketDisplayNameForHeroV2(headlineMarketIdentifier);
  const displayMarketNameInHeadline =
    headlineMarketDisplayName &&
    'showMarketNameInHeadline' in fields &&
    fields.showMarketNameInHeadline &&
    !customization?.headline;
  const headline =
    customization?.headline !== undefined
      ? customization.headline
      : fields?.title + (displayMarketNameInHeadline ? ` in ${headlineMarketDisplayName}` : '');

  const { addressEntryRef, showStickyCTAUtility } = useStickyCTA();

  const hasBothSocialAndPress = fields?.showSocialSection && fields?.showPressSection;

  const variant = customization?.variant || 'default';
  const analyticsPrefix = `cosmos-landing-page-hero-v2${
    fields?.analyticsId ? `-${fields?.analyticsId}` : ''
  }`;

  return (
    <HeroProvider variant={variant} analyticsPrefix={analyticsPrefix}>
      <Box
        analyticsName={
          customization?.analyticsName || `cosmos-landing-page-hero-v2-${fields?.analyticsId}`
        }
        trackImpression
        inViewThreshold={0.3}
      >
        {!hideStickyCta && (
          <StickyCTAUtility analyticsId={fields?.analyticsId} isVisible={showStickyCTAUtility} />
        )}
        {/* Hero container */}
        <Box
          width="90%"
          marginHorizontal="auto"
          paddingHorizontal={isTreatment ? 0 : '$20x'}
          $largerThanSM={{
            paddingBottom: hasBothSocialAndPress ? '$32x' : '$0x',
          }}
        >
          {/* Hero content section */}
          <Box
            flexDirection="column"
            justifyContent={isTreatment ? 'space-around' : 'space-between'}
            rowGap="$20x"
            position="relative"
            $smallerThanSM={{ paddingTop: '$20x' }}
            $largerThanSM={{ flexDirection: 'row', columnGap: '$16x', rowGap: '$8x' }}
          >
            {/* Title and address section */}
            <Box
              $largerThanSM={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                width: '40%',
                flexShrink: 0,
              }}
            >
              <HeroTitleAndAddress
                analyticsId={fields?.analyticsId}
                title={headline}
                subtitle={fields?.subtitle}
                showAddressInput={fields?.showAddressInputCta}
                renderDesktopInput={customization?.renderDesktopInput}
                renderMobileInput={customization?.renderMobileInput}
                additionalAddressContent={customization?.additionalAddressContent}
                addressEntryRef={addressEntryRef}
                addressCtaText={fields?.addressCtaText}
                addressCtaTextSmall={fields?.addressCtaTextSmall}
                addressCtaPlaceholderText={fields?.addressCtaPlaceholderText}
                addressCtaPlaceholderTextSmall={fields?.addressCtaPlaceholderTextSmall}
              />
            </Box>
            {/* Images section */}
            {isTreatment ? (
              <Box
                position="relative"
                $smallerThanMD={{ display: 'none' }}
                $largerThanSM={{ display: 'flex' }}
              >
                <HeroImages
                  imageDesktopUrl={fields?.imageDesktopUrl}
                  imageMobileUrl={fields?.imageMobileUrl}
                  lowBandwidth={customization?.lowBandwidth}
                />
                {customization?.children}
              </Box>
            ) : (
              <Box position="relative" $largerThanSM={{ width: '50%', flexShrink: 0 }}>
                <HeroImages
                  imageDesktopUrl={fields?.imageDesktopUrl}
                  imageMobileUrl={fields?.imageMobileUrl}
                  lowBandwidth={customization?.lowBandwidth}
                />
                {customization?.children}
              </Box>
            )}
            {(fields?.showSocialSection || fields?.showPressSection) && (
              <Box
                id="desktop-press-box"
                position="absolute"
                left="50%"
                transform="translateX(-50%)"
                $largerThanSM={{ bottom: hasBothSocialAndPress ? -100 : -40 }}
                $smallerThanMD={{ position: 'relative' }}
              >
                {fields?.showSocialSection && (
                  <Box $largerThanSM={{ marginHorizontal: 'auto' }}>
                    <SocialProofBox autoplayInterval={3000} />
                  </Box>
                )}
                {fields?.showPressSection && (
                  <Box marginTop="$16x" $smallerThanMD={{ marginTop: 0 }}>
                    <PressBox />
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </HeroProvider>
  );
};

type IHeroV2LoaderReturn = {
  preloadImages: PictureSourceSpec;
  stickyCTA: string | undefined;
};

const heroV2Loader: LoaderFn<ILpComponentHeroV2, IHeroV2LoaderReturn> = async (
  input,
  root?: Entry<ILandingPageFields>,
) => {
  const preloadImages = preloadHeroImages({
    imageDesktopUrl: input.fields.imageDesktopUrl,
    imageMobileUrl: input.fields.imageMobileUrl,
  });
  const loaderReturn: IHeroV2LoaderReturn = {
    preloadImages,
    stickyCTA: getStickyCTAFromRoot(root),
  };
  return loaderReturn;
};

const HeroV2: EntryComponent<ILpComponentHeroV2, Awaited<ReturnType<typeof heroV2Loader>>> = {
  render: (entry, resolvedData) => RenderHeroV2(entry, resolvedData),
  loader: heroV2Loader,
};

export { RenderHeroV2 };
export default HeroV2;
